import { View } from '@play-co/timestep-core/ui';
import StreakPlayerV2 from './StreakPlayerV2';
import gsap from 'gsap';
import { streakBattleRoyaleTheme } from '../streakbattleroyale.theme';
import { XY } from 'src/game/minigame/match2-timestep/odie';
import StreakCloudPlatform from './StreakCloudPlatform';

const slotTransformMap: { x: number; y: number; scale: number }[] = [
  { x: 0, y: 0, scale: 1 },
  { x: -45, y: 0, scale: 0.8 },
  { x: 45, y: -0, scale: 0.8 },
  { x: -75, y: -5, scale: 0.75 },
  { x: 75, y: -5, scale: 0.75 },
  { x: 0, y: -50, scale: 0.72 },
  { x: -55, y: -38, scale: 0.72 },
  { x: 50, y: -35, scale: 0.72 },
];

const firstPlatformScale = 0.8;
const maxDisplayedPlayers = slotTransformMap.length;

export default class StreakPlayerGroupV2 extends View {
  private players: StreakPlayerV2[] = [];
  private disqualifiedPlayers: StreakPlayerV2[] = [];

  constructor(superview: View) {
    super({ superview, zIndex: 5 });
  }

  public setupPlayers(players: { id: string; photo?: string }[]) {
    // remove existing players
    this.players.forEach((player) => player.removeFromSuperview());
    this.players.length = 0;

    const topPlayers = players.slice(0, maxDisplayedPlayers);

    // create and position a fresh batch
    for (let index = 0; index < topPlayers.length; index++) {
      const props = this.getSlotStyleProperties(index);
      const data = topPlayers[index];
      const player = new StreakPlayerV2({
        ...props,
        superview: this,
        playerId: data.id,
        playerPhoto: data.photo,
        zIndex: topPlayers.length - index,
      });

      this.players.push(player);
    }
  }

  public updatePlayers(players: { id: string; photo?: string }[]) {
    const topPlayers = players.slice(0, maxDisplayedPlayers);
    const newPlayers: StreakPlayerV2[] = [];

    // map new players entities
    const updatedPlayerEntities = topPlayers.map((p, i) => {
      const index = this.players.findIndex(
        (player) => player.playerId === p.id,
      );

      if (index !== -1) {
        return this.players[index];
      }

      const newPlayer = new StreakPlayerV2({
        superview: this,
        playerId: p.id,
        playerPhoto: p.photo,
      });

      newPlayers.push(newPlayer);

      return newPlayer;
    });

    // Find all players that are not in the updated list and disqualify them
    this.players.forEach((player) => {
      if (!updatedPlayerEntities.includes(player)) {
        this.disqualifiedPlayers.push(player);
      }
    });

    // Remove disqualified players from the current players array
    this.players = this.players.filter((player) =>
      updatedPlayerEntities.includes(player),
    );

    // Set new players' transform to replace those of disqualified players
    newPlayers.forEach((newPlayer, i) => {
      const eliminatedPlayer =
        this.disqualifiedPlayers[this.disqualifiedPlayers.length - 1 - i];

      if (eliminatedPlayer) {
        const { x, y, zIndex, scale } = eliminatedPlayer.style;

        newPlayer.updateOpts({
          x,
          y,
          zIndex,
          scale,
        });
      }
    });

    // update player entities reference
    this.players = updatedPlayerEntities;
  }

  /**
   * Add all players to the disqualified list
   */
  public disqualifyAll() {
    this.disqualifiedPlayers.push(...this.players);
    this.players.length = 0;
  }

  /**
   * Eliminates all players that are disqualified.
   */
  public async eliminatePlayers(origin: XY, instant = false) {
    const promises: Promise<void>[] = [];

    const players = this.disqualifiedPlayers.slice();
    this.disqualifiedPlayers.length = 0;

    players.forEach((player) => {
      promises.push(player.eliminate(origin, instant));
    });

    await Promise.all(promises);
  }

  public async repositionEliminatedPlayers({
    x,
    y,
    milestone,
    instant = false,
  }: {
    x: number;
    y: number;
    milestone: number;
    instant?: boolean;
  }) {
    const promises: Promise<void>[] = [];

    this.disqualifiedPlayers.forEach((player, i) => {
      player.style.zIndex =
        this.disqualifiedPlayers.length - i + this.players.length;

      player.moveTo({
        ...this.getSlotTransform(x, y, milestone, i),
        instant,
      });
    });

    await Promise.all(promises);
  }

  public async jumpTo({
    platform,
    x,
    y,
    milestone,
    instant = false,
  }: {
    platform?: StreakCloudPlatform;
    x: number;
    y: number;
    milestone: number;
    instant?: boolean;
  }) {
    const promises: Promise<void>[] = [];

    this.players.forEach((player, i) => {
      gsap.killTweensOf(player.style);

      // Set main player zIndex very high to ensure it is always at the top.
      player.style.zIndex = i === 0 ? 100 : this.players.length - i;

      promises.push(
        player.jumpTo({
          platform,
          ...this.getSlotTransform(x, y, milestone, i),
          delay: i * 0.1 + (i > 0 ? 0.4 : 0),
          instant,
        }),
      );
    });

    await Promise.all(promises);

    // Then reset the main player zIndex back to the intended value
    this.players[0].style.zIndex = this.players.length;
  }

  private getSlotStyleProperties(index: number) {
    return (
      slotTransformMap[index] ?? {
        x: 0,
        y: 0,
        scale: 1,
      }
    );
  }

  private getSlotTransform(
    baseX: number,
    baseY: number,
    milestone: number,
    slot: number,
  ) {
    const checkPointScales =
      streakBattleRoyaleTheme().getMapConfig().checkPointScales!;
    const scaleFactor = firstPlatformScale / checkPointScales[0];
    const { x: offsetX, y: offsetY, scale } = this.getSlotStyleProperties(slot);
    const baseScale = milestone
      ? checkPointScales[milestone - 1] * scaleFactor
      : 1;
    const baseOffsetY = milestone * 5;
    const spreadX = milestone > 0 ? 0.55 : 1.2;
    const spreadY = milestone > 0 ? 0.75 : 1;

    return {
      x: baseX + offsetX * baseScale * spreadX,
      y: baseY + baseOffsetY + offsetY * baseScale * spreadY,
      scale: scale * baseScale,
    };
  }
}
